import {SSLAConfiguration} from "../config";
import {ActivityObjective} from "../2004/model";

export type ScoFlatData = {
    status: string;
    normalizedScore: string;
    score: string;
    sessionTime: string;
    totalTime: string;
    sessionTimeInMs: number;
    totalTimeInMs: number;
    sessionWallTime: number;
    totalWallTime: number;
    lastOpenedAt: number;
    lastClosedAt: number;
    data: { [dme: string]: string | number };
};
export type ScosFlatData = {
    status: string;
    score: string;
    normalizedScore: string;
    sessionTime: string;
    totalTime: string;
    sessionTimeInMs: number;
    totalTimeInMs: number;
    currentSco: string;
    sessionWallTime: number;
    totalWallTime: number;
    lastOpenedAt: number;
    lastClosedAt: number;
    scos: { [scoName: string]: ScoFlatData; }
    globalObjectives: { [objName: string]: ActivityObjective; }
}


export class ScoVar {
    protected _value: number | string = "";
    protected dirty: boolean = false;
    protected wasSet: boolean = false;

    constructor(v: string | number = "", protected exportable: string = "always") {
        this._value = v;
    }

    canExport() {
        if (this.exportable == "never") {
            return false;
        }
        if (this.exportable == "always") {
            return true;
        }
        if (this.exportable == "truthy") {
            return !!this._value;
        }
        if (this.exportable == "nonempty") {
            return this._value !== "";
        }
        return false;
    }

    clean() {
        this.dirty = false;
    }

    get(): number | string {
        return this._value;
    }

    set(d: number | string) {
        if (d === undefined || d === null) {
            d = "";
        }
        if (typeof d !== "number") {
            d = String(d);
        }
        if (this._value !== d) {
            this.dirty = true;
        }
        this._value = d;
        this.wasSet = true;
    }

    isDirty(): boolean {
        return this.dirty;
    }

    isSet(): boolean {
        return this.wasSet;
    }
}


export abstract class ScoData {
    public start = false;
    public end = false;
    protected data: { [dme: string]: ScoVar; };
    protected dirty = false;
    protected wasSet = false;
    protected totalWallTimeAtStart = 0;
    protected currentWallTimeStart = 0;
    protected currentWallTimeEnd = 0;

    constructor() {
        this.start = false;
        this.end = false;
        this.data = {};
        this.fillDefaults();
        this.populateMaxes();
    }

    fillDefaults() {
    }

    abstract canImportKey(key: string): boolean;

    abstract getMaxScore(): string;

    abstract getMinScore(): string;

    abstract getNormalizedScore(convertRawToNormalizeMethod: string): string;

    abstract getRawScore(convertNormalizeToRawMethod: string): string;

    abstract getSessionTime(): string;

    abstract getStatus(): string;

    abstract getTotalTime(): string;

    abstract populateMaxes(): void;

    abstract timeInSeconds(time: string): number;

    getMaxIndex(prefix: string): number {
        // Prefix must end in a "." or else this method will not work correctly.
        let i: number,
            prefixLen: number = prefix.length,
            dmeSplit: string[],
            maxIndex = -1,
            dmes: string[] = this.keys(),
            n: number;
        for (i = 0; i < dmes.length; i++) {
            if (dmes[i].substring(0, prefixLen) === prefix) {
                dmeSplit = dmes[i].substring(prefixLen).split(".");
                n = Number(dmeSplit[0]);
                if (!isNaN(n)) {
                    maxIndex = Math.max(n, maxIndex);
                }
            }
        }
        return maxIndex;
    }

    exportData(config: SSLAConfiguration, clean: boolean): ScoFlatData {
        let flatData: { [dme: string]: string | number; } = {};
        let scoFlat: ScoFlatData = {
            status: this.getStatus(),
            normalizedScore: this.getNormalizedScore(config.scoreConvertRawToNormalizedMethod()),
            score: this.getRawScore(config.scoreConvertNormalizedToRawMethod()),
            sessionTime: this.getSessionTime(),
            totalTime: this.getTotalTime(),
            sessionTimeInMs: Math.floor(this.timeInSeconds(this.getSessionTime()) * 1000),
            totalTimeInMs: Math.floor(this.timeInSeconds(this.getTotalTime()) * 1000),
            sessionWallTime: 0,
            totalWallTime: 0,
            lastOpenedAt: this.currentWallTimeStart,
            lastClosedAt: this.currentWallTimeEnd,
            data: flatData
        };

        // If we've ended the session, use the known session time.  Otherwise calculate from current time against
        // the last session start time, to give us as close to a current point as we can.
        // This differs from SCORM logic, where these values are generally only updated at the end of the SCO.
        if (this.currentWallTimeEnd) {
            scoFlat.sessionWallTime = this.currentWallTimeEnd - this.currentWallTimeStart;
        } else if (this.currentWallTimeStart) {
            scoFlat.sessionWallTime = new Date().getTime() - this.currentWallTimeStart;
        }
        scoFlat.totalWallTime = this.totalWallTimeAtStart + scoFlat.sessionWallTime;

        let k: string;
        for (k in this.data) {
            if (this.data.hasOwnProperty(k)) {
                if (this.data[k].canExport()) {
                    flatData[k] = this.data[k].get();
                    if (clean) {
                        this.data[k].clean();
                    }
                }
            }
        }
        return scoFlat;
    };

    importData(unflattenData: ScoFlatData) {
        let k: string;
        for (k in unflattenData.data) {
            if (unflattenData.data.hasOwnProperty(k)) {
                if (this.canImportKey(k)) {
                    // Intentionally prevent dirty/isSet flag setting on import.
                    this.data[k] = new ScoVar(unflattenData.data[k]);
                }
            }
        }
        this.populateMaxes();
        this.totalWallTimeAtStart = 0;
        if (unflattenData.totalWallTime) {
            this.totalWallTimeAtStart = unflattenData.totalWallTime;
        }
    }

    clear(dme: string) {
        delete this.data[dme];
    }

    exists(dme: string): boolean {
        return typeof this.data[dme] !== "undefined";
    }

    get(dme: string): string | number {
        if (!this.data[dme]) {
            return "";
        }
        return this.data[dme].get();
    }

    set(dme: string, value: string) {
        if (!this.data[dme]) {
            this.data[dme] = new ScoVar();
        }
        this.data[dme].set(value);
        this.dirty = this.dirty || this.data[dme].isDirty();
        this.wasSet = true;
    }

    clean() {
        const keys = Object.keys(this.data);
        for (let key of keys) {
            this.data[key].clean();
        }
        this.dirty = false;
    }

    isDirty(): boolean {
        return this.dirty;
    }

    isSet(): boolean {
        return this.wasSet;
    }

    keys(): string[] {
        return Object.keys(this.data);
    }

    startViewing(): void {
        this.currentWallTimeStart = new Date().getTime();
        this.currentWallTimeEnd = 0;
    }

    stopViewing(): void {
        this.currentWallTimeEnd = new Date().getTime();
    }
}


export abstract class AllScosData {
    public scos: { [scoName: string]: ScoData; } = {};
    public currentScoId: string = null;
    protected sessionTime: string = "";
    protected totalTime: string = "";
    protected sessionTimeInMs: number = 0;
    protected totalTimeInMs: number = 0;
    protected currentWallTimeStart: number = 0;
    protected currentWallTimeEnd: number = 0;
    protected totalWallTimeAtStart: number = 0;
    protected status: string = "";
    protected score: string = "";
    protected normalizedScore: string = "";
    public globalObjectives: { [objName: string]: ActivityObjective; } = {};

    abstract addTimes(one: string, two: string): string;

    abstract makeNewScoData(): ScoData;

    // Reset all dirty flags so that isDirty returns false.
    clean() {
        const keys = Object.keys(this.scos);
        for (let key of keys) {
            this.scos[key].clean();
        }
    }

    exportData(config: SSLAConfiguration, clean: boolean): ScosFlatData {
        let d: ScosFlatData = {
            status: "",
            score: "",
            normalizedScore: "",
            sessionTime: "",
            totalTime: "",
            sessionTimeInMs: 0,
            totalTimeInMs: 0,
            sessionWallTime: 0,
            totalWallTime: 0,
            lastOpenedAt: this.currentWallTimeStart,
            lastClosedAt: this.currentWallTimeEnd,
            currentSco: this.currentScoId,
            scos: {},
            globalObjectives: {},
        };
        let k: string;

        // If we've ended the session, use the known session time.  Otherwise calculate from current time against
        // the last session start time, to give us as close to a current point as we can.
        // This differs from SCORM logic, where these values are generally only updated at the end of the SCO.
        if (this.currentWallTimeEnd) {
            d.sessionWallTime = this.currentWallTimeEnd - this.currentWallTimeStart;
        } else {
            d.sessionWallTime = new Date().getTime() - this.currentWallTimeStart;
        }
        d.totalWallTime = this.totalWallTimeAtStart + d.sessionWallTime;

        for (k in this.scos) {
            if (this.scos.hasOwnProperty(k)) {
                d.scos[k] = this.scos[k].exportData(config, clean);
                d.sessionTime = this.addTimes(this.scos[k].getSessionTime(), d.sessionTime);
                d.totalTime = this.addTimes(this.scos[k].getTotalTime(), d.totalTime);
                d.sessionTimeInMs = Math.floor(this.scos[k].timeInSeconds(d.sessionTime) * 1000);
                d.totalTimeInMs = Math.floor(this.scos[k].timeInSeconds(d.totalTime) * 1000);
            }
            // TODO This feels hacky, but is necessary now that we no longer have something to trigger storage
            // sometime after a finish.
            if (d.lastClosedAt < d.scos[k].lastClosedAt) {
                d.lastClosedAt = d.scos[k].lastClosedAt;
            }
        }
        d.status = this.status;
        d.score = this.score;
        d.normalizedScore = this.normalizedScore;
        if (Object.keys(this.globalObjectives).length > 0) {
            d.globalObjectives = this.globalObjectives;
        }
        return d;
    };

    getOrCreate(identifier: string): ScoData {
        if (!(identifier in this.scos)) {
            this.scos[identifier] = this.makeNewScoData();
        }
        return this.scos[identifier];
    }

    importData(unflattenData: ScosFlatData) {
        let k: string;
        this.currentScoId = unflattenData.currentSco;
        for (k in unflattenData.scos) {
            if (unflattenData.scos.hasOwnProperty(k)) {
                this.scos[k] = this.makeNewScoData();
                this.scos[k].importData(unflattenData.scos[k]);
            }
        }
        if (unflattenData.globalObjectives) {
            this.globalObjectives = unflattenData.globalObjectives;
        }
        this.totalWallTimeAtStart = 0;
        if (unflattenData.totalWallTime) {
            this.totalWallTimeAtStart = unflattenData.totalWallTime;
        }
        this.score = typeof unflattenData.score != "undefined" ? unflattenData.score : "";
        this.status = typeof unflattenData.status != "undefined" ? unflattenData.status : "";
        this.sessionTime = "";
        this.totalTime = "";
        if (unflattenData.totalTime) {
            this.totalTime = unflattenData.totalTime;
        }
        this.sessionTimeInMs = typeof unflattenData.sessionTimeInMs != "undefined" ? unflattenData.sessionTimeInMs : 0;
        this.totalTimeInMs = typeof unflattenData.totalTimeInMs != "undefined" ? unflattenData.totalTimeInMs : 0;
    }

    isDirty(): boolean {
        const keys = Object.keys(this.scos);
        for (let key of keys) {
            if (this.scos[key].isDirty()) {
                return true;
            }
        }
        return false;
    }

    setRollupScore(score: string): void {
        this.score = score;
    }

    setRollupNormalizedScore(score: string): void {
        this.normalizedScore = score;
    }

    setRollupStatus(status: string): void {
        this.status = status;
    }

    startViewing(): void {
        this.currentWallTimeStart = new Date().getTime();
        this.currentWallTimeEnd = 0;
    }

    updateSessionViewing(): void {
        this.currentWallTimeEnd = new Date().getTime();
    }
}
